import { Link } from 'react-router-dom';
import { ArrowRight, CircleAlert } from 'lucide-react';
import { CardStatus, KycStatus, type KycSnapshot } from '@shared/types';
import { cn } from '@/lib/cn';

/**
 * The one thing standing between a trader and earning anything.
 *
 * Since the tab bar dropped to three tabs, this is also the *only* route to the
 * documents screen — so on Обзор it is rendered at `page` size: a real heading,
 * a filled call to action, and no competition from anything above the balance.
 * On Трафик it stays at `inline` size, where it is context rather than the task.
 *
 * It disappears the moment `canReceiveTraffic` turns true. A trader who
 * finished months ago should never see a permanent "verified" plaque; the
 * absence of this panel is the confirmation.
 */
export type PromptVariant = 'page' | 'inline';

export function VerificationPrompt({
  kyc,
  variant = 'inline',
  className,
}: {
  kyc: KycSnapshot;
  variant?: PromptVariant;
  className?: string;
}) {
  // `canReceiveTraffic` is the server's own gate — approved KYC *and* at least
  // one verified card. Re-deriving that rule here is how the two drift apart.
  if (kyc.canReceiveTraffic) return null;

  const content = describe(kyc);

  return variant === 'page' ? (
    <PagePrompt content={content} className={className} />
  ) : (
    <InlinePrompt content={content} className={className} />
  );
}

interface PromptContent {
  title: string;
  /** Shown above the checklist; the rejection reason when there is one. */
  reason: string | null;
  body: string;
  missing: string[];
  destination: string;
  action: string;
  isRejected: boolean;
}

/**
 * Everything the two renderings disagree about is size, so the wording is
 * decided once here and neither variant can say something the other doesn't.
 */
function describe(kyc: KycSnapshot): PromptContent {
  const missing = missingSteps(kyc);
  const isRejected = kyc.status === KycStatus.Rejected;
  const isApproved = kyc.status === KycStatus.Approved;
  const isAwaitingReview = kyc.status === KycStatus.Pending && missing.length === 0;

  // An approved account that still cannot take traffic is missing a *verified
  // card*, and the documents screen cannot fix that — send them to «Реквизиты».
  // The document flags are checked too, so an unexpected server state falls
  // back to the documents screen rather than to a dead end.
  const isCardOnly = isApproved && kyc.passportUploaded && kyc.selfieUploaded;

  return {
    title: isRejected
      ? 'Верификация отклонена'
      : isCardOnly
        ? 'Нужна проверенная карта'
        : isAwaitingReview
          ? 'Верификация на проверке'
          : 'Верификация не завершена',

    reason: isRejected ? kyc.rejectionReason : null,

    body: isCardOnly
      ? 'Документы одобрены. Платежи пойдут, как только хотя бы одна карта пройдёт проверку.'
      : isAwaitingReview
        ? 'Всё отправлено. Трафик пойдёт, как только администратор одобрит аккаунт — обычно в течение рабочего дня.'
        : 'Пока это не завершено, платежи вам направляться не будут.',

    missing,
    destination: isCardOnly ? '/cards' : '/verification',
    action: isRejected
      ? 'Отправить документы заново'
      : isCardOnly
        ? 'Перейти к реквизитам'
        : 'Перейти к верификации',
    isRejected,
  };
}

/**
 * Overview treatment. Larger type, more air, and a filled button rather than a
 * text link — a trader who cannot earn should not have to hunt for the way out.
 */
function PagePrompt({ content, className }: { content: PromptContent; className?: string }) {
  const { isRejected } = content;

  return (
    <aside
      className={cn(
        'rounded-md border px-4 py-4 sm:px-5 sm:py-5',
        isRejected ? 'border-danger-500 bg-danger-50' : 'border-warn-500 bg-warn-50',
        className,
      )}
    >
      <div className="flex items-start gap-3">
        <CircleAlert
          size={20}
          strokeWidth={1.5}
          aria-hidden="true"
          className={cn('mt-0.5 shrink-0', isRejected ? 'text-danger-500' : 'text-warn-700')}
        />

        <div className="min-w-0 flex-1">
          <h2
            className={cn(
              'text-lg font-semibold leading-snug tracking-tight',
              isRejected ? 'text-danger-700' : 'text-warn-700',
            )}
          >
            {content.title}
          </h2>

          {content.reason ? (
            <p className="mt-1.5 max-w-prose text-base leading-normal text-danger-700">
              {content.reason}
            </p>
          ) : null}

          <p className="mt-1.5 max-w-prose text-base leading-normal text-fg-secondary">
            {content.body}
          </p>

          {content.missing.length > 0 ? (
            <ul className="mt-3 space-y-1.5">
              {content.missing.map((step) => (
                <li
                  key={step}
                  className="flex max-w-prose items-start gap-2.5 text-base leading-normal text-fg-secondary"
                >
                  <span
                    aria-hidden="true"
                    className={cn(
                      'mt-2 h-1.5 w-1.5 shrink-0 rounded-full',
                      isRejected ? 'bg-danger-500' : 'bg-warn-700',
                    )}
                  />
                  {step}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </div>

      {/* Full width on a phone so the target is unmissable, and outside the icon
          column so a long Russian label has the whole panel to sit in. Above
          640px it shrinks to its label and indents by the icon column's width
          (20px icon + 12px gap) to line up under the heading. */}
      <Link
        to={content.destination}
        className={cn(
          'focus-ring mt-4 flex h-11 w-full select-none items-center justify-center gap-2',
          'rounded-md border border-accent-600 bg-accent-600 px-4 text-base font-medium',
          'text-fg-inverse no-underline transition-colors duration-fast ease-out',
          'hover:border-accent-700 hover:bg-accent-700',
          'active:border-accent-900 active:bg-accent-900',
          'sm:ml-8 sm:inline-flex sm:w-auto sm:px-5',
        )}
      >
        {content.action}
        <ArrowRight size={15} strokeWidth={1.5} aria-hidden="true" />
      </Link>
    </aside>
  );
}

/**
 * Traffic treatment, unchanged from when this was the only one: amber, not red,
 * because nothing is broken — the account simply isn't finished.
 */
function InlinePrompt({ content, className }: { content: PromptContent; className?: string }) {
  const { isRejected } = content;

  return (
    <aside
      className={cn(
        'rounded-md border px-4 py-3.5',
        isRejected ? 'border-danger-500 bg-danger-50' : 'border-warn-500 bg-warn-50',
        className,
      )}
    >
      <div className="flex items-start gap-3">
        <CircleAlert
          size={16}
          strokeWidth={1.5}
          aria-hidden="true"
          className={cn('mt-0.5 shrink-0', isRejected ? 'text-danger-500' : 'text-warn-700')}
        />

        <div className="min-w-0 flex-1">
          <p
            className={cn(
              'text-base font-semibold',
              isRejected ? 'text-danger-700' : 'text-warn-700',
            )}
          >
            {content.title}
          </p>

          {content.reason ? (
            <p className="mt-1 max-w-prose text-sm leading-normal text-danger-700">
              {content.reason}
            </p>
          ) : null}

          <p className="mt-1 max-w-prose text-sm leading-normal text-fg-secondary">
            {content.body}
          </p>

          {content.missing.length > 0 ? (
            <ul className="mt-2.5 space-y-1">
              {content.missing.map((step) => (
                <li key={step} className="flex items-start gap-2 text-sm text-fg-secondary">
                  <span
                    aria-hidden="true"
                    className="mt-2 h-1 w-1 shrink-0 rounded-full bg-fg-muted"
                  />
                  {step}
                </li>
              ))}
            </ul>
          ) : null}

          <Link
            to={content.destination}
            className={cn(
              'focus-ring mt-3 inline-flex min-h-11 items-center gap-1.5 rounded-sm',
              'text-sm font-medium underline underline-offset-2',
              isRejected ? 'text-danger-700' : 'text-warn-700',
            )}
          >
            {content.action}
            <ArrowRight size={13} strokeWidth={1.5} aria-hidden="true" />
          </Link>
        </div>
      </div>
    </aside>
  );
}

/** Plain-language list of what is still outstanding, in the order it's done. */
function missingSteps(kyc: KycSnapshot): string[] {
  const steps: string[] = [];

  if (!kyc.passportUploaded) {
    steps.push('Фото паспорта не загружено.');
  }
  if (!kyc.selfieUploaded) {
    steps.push('Селфи с паспортом не загружено.');
  }
  if (kyc.cards.length === 0) {
    steps.push('Не добавлена ни одна банковская карта.');
  } else if (!kyc.cards.some((card) => card.status === CardStatus.Verified)) {
    const rejected = kyc.cards.filter((card) => card.status === CardStatus.Rejected).length;
    steps.push(
      rejected === kyc.cards.length
        ? 'Все добавленные карты отклонены. Добавьте карту, оформленную на ваше имя.'
        : 'Ни одна карта пока не прошла проверку.',
    );
  }
  if (kyc.status === KycStatus.NotStarted && steps.length === 0) {
    steps.push('Документы не отправлены на проверку.');
  }

  return steps;
}
